import {
    DropDown,
    Popover,
    useErrorState,
    ValidatedDropDownList,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { getSelectList } from 'axios/dictionaryApi';
import { cancelOrder } from 'axios/orderApi';
import useApi from 'hooks/useApi';
import { Dictionary } from 'lodash';
import { OrderStatusChange } from 'models/api/order';
import { RefObject, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { ButtonProps } from 'reactstrap';

type OrderCancelPopoverProps = {
    currentStatus: string;
    orderId: string;
    withIcon?: boolean;
    popoverBtn?: boolean;
    buttonProps?: ButtonProps;
    popoverContainer?: string | HTMLElement | RefObject<HTMLElement>;
    mutateCallback?: () => void;
};

const OrderCancelPopoverWrapped = () => {
    const api = useApi();
    const { t, i18n } = useTranslation();
    const { watch, setValue } = useFormContext<OrderStatusChange>();
    const [data, setData] = useState<Dictionary<DropDown[]> | null>(null);

    const { isFetching } = useQuery(
        'orderCancelColReasons',
        () =>
            getSelectList(
                api,
                [
                    'CancelCcReasonWrongInformation',
                    'CancelCcReasonForTime',
                    'CancelCcReasonPhotos',
                    'CancelCcReasonPossibleFraud',
                    'CancelCcReasonClientsPetition',
                    'CancelCcReasonClientsPetitionVeryLowApprovedAmount',
                    'CancelCcReasonClientsPetitionYouDontNeedItAnymore',
                    'CancelCcReasonClientsPetitionTypingError',
                    'CancelCcReasonClientsPetitionOthers',
                ],
                i18n.language
            ),
        {
            onSuccess: (res) => {
                setData(res);
            },
            enabled: !data,
        }
    );

    const reason = watch('reason');
    const reasonLvl2 = watch('reasonLvl2');

    const reasonOptionsLvl2 = useMemo(() => {
        let dictName: string;

        switch (reason) {
            case 'WrongInformation':
                dictName = 'CancelCcReasonWrongInformation';
                break;
            case 'ForTime':
                dictName = 'CancelCcReasonForTime';
                break;
            case 'Photos':
                dictName = 'CancelCcReasonPhotos';
                break;
            case 'PossibleFraud':
                dictName = 'CancelCcReasonPossibleFraud';
                break;
            case 'ClientsPetition':
                dictName = 'CancelCcReasonClientsPetition';
                break;
            default:
                return [];
        }
        const res = (data && data[dictName]) || [];
        setValue('reasonLvl2', res[0]?.id?.toString() ?? '');
        if (reason !== 'ClientsPetition') setValue('reasonLvl3', '');
        return res;
    }, [reason, data]);

    const reasonOptionsLvl3 = useMemo(() => {
        let dictName: string;

        switch (reasonLvl2) {
            case 'VeryLowApprovedAmount':
                dictName = 'CancelCcReasonClientsPetitionVeryLowApprovedAmount';
                break;
            case 'YouDontNeedItAnymore':
                dictName = 'CancelCcReasonClientsPetitionYouDontNeedItAnymore';
                break;
            case 'TypingError':
                dictName = 'CancelCcReasonClientsPetitionTypingError';
                break;
            case 'Others':
                dictName = 'CancelCcReasonClientsPetitionOthers';
                break;
            default:
                return [];
        }
        const res = (data && data[dictName]) || [];
        setValue('reasonLvl3', res[0]?.id?.toString() ?? '');
        return res;
    }, [reasonLvl2, data]);

    return (
        <>
            <ValidatedDropDownList<OrderStatusChange>
                items={reasonOptionsLvl2}
                name="reasonLvl2"
                label={t('BackOfficeReason')}
                isLoading={isFetching}
                isClearable={false}
                menuPortalTarget={null}
            />
            <ValidatedDropDownList<OrderStatusChange>
                items={reasonOptionsLvl3}
                name="reasonLvl3"
                label={t('BackOfficeReason')}
                isLoading={isFetching}
                isClearable={false}
                disabled={reason !== 'ClientsPetition'}
                menuPortalTarget={null}
            />
        </>
    );
};

const OrderCancelPopover = ({
    currentStatus,
    orderId,
    withIcon = true,
    popoverBtn,
    buttonProps,
    popoverContainer,
    mutateCallback,
}: OrderCancelPopoverProps) => {
    const api = useApi();
    const { t, i18n } = useTranslation();
    const { handleRequestError } = useErrorState();

    const { data, isFetching } = useQuery(
        'orderCancelReasons',
        () => getSelectList(api, ['CancelCcReason'], i18n.language),
        {
            onError: (err) => {
                handleRequestError(err);
            },
        }
    );

    const reasonsList = useMemo(() => (data && data['CancelCcReason']) || [], [data]);

    return (
        <Popover<OrderStatusChange>
            label="close"
            textStyle="danger"
            buttonIcon={withIcon ? faTrashCan : undefined}
            buttonLabel="BackOfficeCancelOrder"
            orderId={orderId}
            mutationFunction={(data) => cancelOrder(api, data).then(() => mutateCallback?.())}
            popoverBtn={popoverBtn}
            buttonProps={buttonProps}
            popoverContainer={popoverContainer}
            canSave={true}
            refetchQueryKeys={[['order'], 'orderStatusCount']}
            header={
                <>
                    <Trans i18nKey={'BackOfficeCancelOrder'} /> #{orderId}
                </>
            }
            defaultValues={{
                currentStatus: currentStatus,
                orderId: orderId,
                status: 'cccancel',
                timeout: undefined,
                reason: reasonsList[0]?.id?.toString(),
                reasonLvl2: '',
                reasonLvl3: '',
                dsc: '',
            }}
        >
            <ValidatedDropDownList<OrderStatusChange>
                items={reasonsList}
                name="reason"
                label={t('BackOfficeReason')}
                isLoading={isFetching}
                isClearable={false}
                menuPortalTarget={null}
            />
            <OrderCancelPopoverWrapped />
            <ValidatedInput<OrderStatusChange>
                id="info-comment-field"
                name="dsc"
                type="textarea"
                label={t('BackOfficeComment')}
            />
        </Popover>
    );
};

export default OrderCancelPopover;
